import { createRoute, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router';
import { Loading } from '../components/loading';
import { RouteError } from '../components/route-errors';
import { isRBGVendor } from '../data/vendor';
import {
  exceptionLogsQueryOptions,
  productsQueryOptions,
  receiptsQueryOptions,
  vendorsQueryOptions,
} from '../query/query-options';
import { storeLayout } from './store';

// ---------------------------------------------------------------------------
// Reports layout (/reports) — loads products
// ---------------------------------------------------------------------------

const reportsLayout = createRoute({
  getParentRoute: () => storeLayout,
  path: '/reports',
  component: lazyRouteComponent(() => import('../pages/reports/index'), 'ReportsLayout'),
  errorComponent: RouteError,
});

const reportsIndexRoute = createRoute({
  getParentRoute: () => reportsLayout,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/stores/$storeId/reports/popularity',
      params: { storeId: params.storeId },
    });
  },
});

// Pathless layout that prefetches products for product-dependent reports.
// Sales Journal doesn't need products, so it's a sibling.
const productReportsLayout = createRoute({
  getParentRoute: () => reportsLayout,
  id: 'productReportsLayout',
  component: Outlet,
  pendingComponent: Loading,
  pendingMs: 200,
  pendingMinMs: 300,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...productsQueryOptions(context.store.id),
      queryFn: () => context.productService.getProductsByStore(context.store.id),
    }),
});

const popularityRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/popularity',
  component: lazyRouteComponent(() => import('../pages/reports/popularity'), 'PopularityReport'),
});

const stockingIssuesRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/stocking-issues',
  component: lazyRouteComponent(
    () => import('../pages/reports/stocking-issues'),
    'StockingIssuesReport'
  ),
});

const phantomInventoryRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/phantom-inventory',
  component: lazyRouteComponent(
    () => import('../pages/reports/phantom-inventory'),
    'PhantomInventoryReport'
  ),
});

const underperformingInventoryRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/underperforming-inventory',
  component: lazyRouteComponent(
    () => import('../pages/reports/underperforming-inventory'),
    'UnderperformingInventoryReport'
  ),
});

const negativeInventoryRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/negative-inventory',
  component: lazyRouteComponent(
    () => import('../pages/reports/negative-inventory'),
    'NegativeInventoryReport'
  ),
});

const previousDaySalesRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/previous-day-sales',
  component: lazyRouteComponent(
    () => import('../pages/reports/previous-day-sales'),
    'PreviousDaySalesReport'
  ),
});

const lowStockRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/low-stock',
  component: lazyRouteComponent(() => import('../pages/reports/low-stock'), 'LowStockReport'),
});

// Temporary (ORD-567): RBG's item-code cutover. Loads vendors to find the
// store's RBG vendors; a store without one (reached by URL, or by switching
// stores while on the report) goes back to the reports index, as its tab is
// hidden too.
const rbgItemCodesRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/rbg-item-codes',
  component: lazyRouteComponent(
    () => import('../pages/reports/rbg-item-codes'),
    'RBGItemCodesReport'
  ),
  loader: async ({ context, params }) => {
    const vendors = await context.queryClient.ensureQueryData({
      ...vendorsQueryOptions(context.store.id),
      queryFn: () => context.vendorService.getVendorsByStore(context.store.id),
    });

    if (!vendors.some(isRBGVendor)) {
      throw redirect({ to: '/stores/$storeId/reports', params: { storeId: params.storeId } });
    }
  },
});

const exceptionLogsRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/exception-logs',
  component: lazyRouteComponent(
    () => import('../pages/reports/exception-logs'),
    'ExceptionLogsReport'
  ),
  validateSearch: (search: Record<string, unknown>): { query?: string } => ({
    query: typeof search.query === 'string' ? search.query : undefined,
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...exceptionLogsQueryOptions(context.store.id),
      queryFn: () => context.exceptionLogService.getExceptionLogsByStore(context.store.id),
    }),
});

const salesJournalRoute = createRoute({
  getParentRoute: () => reportsLayout,
  path: '/sales-journal',
  component: lazyRouteComponent(
    () => import('../pages/reports/sales-journal'),
    'SalesJournalReport'
  ),
  validateSearch: (
    search: Record<string, unknown>
  ): { from?: string; to?: string; receipt?: string } => ({
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    receipt: search.receipt != null ? String(search.receipt) : undefined,
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...receiptsQueryOptions(context.store.id),
      queryFn: () => context.receiptService.getReceipts(context.store.id),
    }),
});

export const reportRoutes = reportsLayout.addChildren([
  reportsIndexRoute,
  productReportsLayout.addChildren([
    popularityRoute,
    stockingIssuesRoute,
    phantomInventoryRoute,
    underperformingInventoryRoute,
    negativeInventoryRoute,
    previousDaySalesRoute,
    lowStockRoute,
    rbgItemCodesRoute,
    exceptionLogsRoute,
  ]),
  salesJournalRoute,
]);
